import { useNavigate, useParams } from 'react-router-dom'
import { faArrowLeft, faPrint } from '@fortawesome/pro-duotone-svg-icons'

import Button from '../../components/button/Button'
import Icon from '../../components/icon/Icon'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import {
  formatCents,
  formatLedgerAcknowledgement,
  formatLedgerDate,
  formatLedgerSource,
  formatLedgerStatus,
} from '../../_utilities/resident-format'
import {
  ReceiptRoot,
  ReceiptActions,
  ReceiptCard,
  ReceiptHeader,
  ReceiptBrand,
  ReceiptTitle,
  ReceiptAmountRow,
  ReceiptAmountLabel,
  ReceiptAmount,
  ReceiptRowGroup,
  ReceiptRow,
  ReceiptLabel,
  ReceiptValue,
  ReceiptFootnote,
  NotFoundCard,
  QuietText,
} from './styled'

/**
 * A printable receipt for a single ledger entry, reached from the Payments
 * ledger ("View receipt"). Rendered as a standalone, chrome-free page so the
 * browser's Print / Save-as-PDF produces a clean document (the action buttons
 * hide under `_print`). The entry is read from the shared resident ledger that's
 * already in memory — there is no per-entry backend fetch — so a fresh deep-link
 * with no loaded ledger falls through to the not-found state.
 *
 * @returns {JSX.Element}
 */
const ReceiptPage = (): JSX.Element => {
  const navigate = useNavigate()
  const { entryId } = useParams<{ entryId: string }>()
  const { ledger, selectedTenancy, isLoadingTenancies, isLoadingLedger } =
    useResidentTenancy()

  const isResolving = isLoadingTenancies === true || isLoadingLedger === true
  const entry =
    ledger === null
      ? null
      : (ledger.entries.find(item => item.id === entryId) ?? null)

  if (entry === null) {
    const notFoundMessage = isResolving
      ? 'Loading your receipt…'
      : 'We couldn’t find that receipt. If it’s for a different residence, switch to that residence on Payments and open the receipt from there.'

    return (
      <ReceiptRoot>
        <ReceiptActions>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => navigate('/payments')}
          >
            <Icon icon={faArrowLeft} /> Back to payments
          </Button>
        </ReceiptActions>
        <NotFoundCard>
          <QuietText>{notFoundMessage}</QuietText>
        </NotFoundCard>
      </ReceiptRoot>
    )
  }

  // For a payment recorded by hand the useful reference is the one the payer can
  // check against their own records, an e-transfer confirmation or a cheque
  // number, not our document id. The document id stays as the last resort.
  const reference =
    entry.offlineReference ??
    entry.stripePaymentIntentId ??
    entry.stripeChargeId ??
    entry.id

  // The date that matters on a receipt is when the money changed hands, which for
  // a recorded payment is not when somebody got around to entering it.
  const paidOn =
    typeof entry.receivedOn === 'number' ? entry.receivedOn : entry.createdAt

  const acknowledgement = formatLedgerAcknowledgement(entry)

  return (
    <ReceiptRoot>
      <ReceiptActions>
        <Button
          type="button"
          variant="secondary"
          size="small"
          onClick={() => navigate('/payments')}
        >
          <Icon icon={faArrowLeft} /> Back to payments
        </Button>
        <Button
          type="button"
          variant="primary"
          size="small"
          onClick={() => window.print()}
        >
          <Icon icon={faPrint} /> Print / Save PDF
        </Button>
      </ReceiptActions>

      <ReceiptCard>
        <ReceiptHeader>
          <ReceiptBrand>QuickCasa Resident</ReceiptBrand>
          <ReceiptTitle>Payment receipt</ReceiptTitle>
        </ReceiptHeader>

        <ReceiptAmountRow>
          <ReceiptAmountLabel>Amount</ReceiptAmountLabel>
          <ReceiptAmount>{formatCents(entry.amountCents)}</ReceiptAmount>
        </ReceiptAmountRow>

        <ReceiptRowGroup>
          {selectedTenancy !== null ? (
            <ReceiptRow>
              <ReceiptLabel>Residence</ReceiptLabel>
              <ReceiptValue>{selectedTenancy.unitLabel}</ReceiptValue>
            </ReceiptRow>
          ) : null}
          <ReceiptRow>
            <ReceiptLabel>Date</ReceiptLabel>
            <ReceiptValue>{formatLedgerDate(paidOn)}</ReceiptValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptLabel>Description</ReceiptLabel>
            <ReceiptValue>{entry.description}</ReceiptValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptLabel>Paid by</ReceiptLabel>
            <ReceiptValue>{formatLedgerSource(entry)}</ReceiptValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptLabel>Status</ReceiptLabel>
            <ReceiptValue>{formatLedgerStatus(entry.status)}</ReceiptValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptLabel>Reference</ReceiptLabel>
            <ReceiptValue>{reference}</ReceiptValue>
          </ReceiptRow>
          {acknowledgement !== '' ? (
            <ReceiptRow>
              <ReceiptLabel>Confirmed</ReceiptLabel>
              <ReceiptValue>{acknowledgement}</ReceiptValue>
            </ReceiptRow>
          ) : null}
        </ReceiptRowGroup>

        <ReceiptFootnote>
          This receipt was generated by QuickCasa Resident as a record of your
          payment.
        </ReceiptFootnote>
      </ReceiptCard>
    </ReceiptRoot>
  )
}

export default ReceiptPage
